import { createContext, ReactNode, useEffect, useState } from 'react'
import { GET, POST } from '../services/api'
import useAuth from '../hooks/useAuth';
import { useLocalStorage } from './localstorageContext';



type LocationType = {
    id: number;
    name: string;
    code: string;
    status: boolean;
    type: string;
    cep: string;
    address: any | null;
};

type MetaType = {
    hasNextPage: boolean
    hasPreviousPage: boolean
    itemCount: number
    page: number
    pageCount: number
    take: number
}

type PluginSpaceContextData = {
    pluginSpace: any
    getPluginSpace: (fromApi?: boolean, useLocalStorage?: boolean) => any
    locations: LocationType[]
    getLocations: (fromApi?: boolean, useLocalStorage?: boolean) => Promise<LocationType[] | any[]>
    menu: MenuType[]
    getMenu: (fromApi?: boolean) => Promise<MenuType[] | any[]>
    manual: Manual[]
    integrations: IntegrationType[]
    getIntegrations: (value?: boolean, useLocalStorage?: boolean) => Promise<IntegrationType[]>
    isProtheus: boolean
    isWintor: boolean
    hasERP: boolean
    useTotvs: boolean
    usingSynkar: boolean
    usingSDS: boolean
    getPluginConfigs: (value?: boolean, useLocalStorage?: boolean) => Promise<IntegrationResponseType[]>
    metaPluginConfigs: MetaType | undefined,
    loadingGlobal: boolean
    setLoadingGlobal: (value: boolean) => void
    getGmtStore: () => Promise<string>,
    alterMenu: number,
    setAlterMenu: (value: number) => void
}

type PluginSpaceContextProvider = {
    children: ReactNode
}


type MenuType = {
    icon: string
    id: number
    name: string
    newIcon: string
    order: number
    parent: number
    route: string
    submenu: SubMenuType[]
    type: string
    permissionParent?: boolean
}

type SubMenuType = {
    id: number
    name: string
    newIcon: string
    order: number
    parent: number
    router: string
}

type Manual = {
    id: number;
    name: string;
    type: string;
    route: string;
    created?: any;
    updated?: any;
}

export type IntegrationType = {
    id: number
    name: string
    plugintemplateId: number
    content: any
    deleted: string
    plugintypeId: number

}
type IntegrationResponseType = {
    id: number;
    name: string;
    plugintemplateId: {
        id: number;
        name: string;
        parameters: string;
        plugintypeId: number;
        typeIntegration: string;
    }
}



export const PluginSpaceContext = createContext({} as PluginSpaceContextData)

const PluginSpaceProvider = ({ children }: PluginSpaceContextProvider) => {

    const [pluginSpace, setPluginSpace] = useState<any>(null)
    const [locations, setLocations] = useState<LocationType[]>([])
    const [menu, setMenu] = useState<MenuType[]>([])
    const [manual, setManual] = useState<Manual[]>([])
    const [integrations, setIntegrations] = useState<IntegrationType[]>([])
    const [pluginConfigs, setPluginConfigs] = useState<IntegrationResponseType[]>([])
    const [isWintor, setIsWintor] = useState<boolean>(false)
    const [isProtheus, setIsProtheus] = useState<boolean>(false)
    const [hasERP, setHasERP] = useState<boolean>(false)
    const [useTotvs, setUseTotvs] = useState<boolean>(false)
    const [usingSDS, setUsingSDS] = useState<boolean>(false)
    const [usingSynkar, setUsingSynkar] = useState<boolean>(false)
    const [metaPluginConfigs, setMetaPluginConfigs] = useState<MetaType | undefined>(undefined)
    const { loginInfo } = useAuth()
    const { saveItem, getItem } = useLocalStorage()

    const [alterMenu, setAlterMenu] = useState<number>(0)

    const [loadingGlobal, setLoadingGlobal] = useState<boolean>(true)
    const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false)
    const [currentPluginSpaceId, setCurrentPluginSpaceId] = useState<number | null>(null)
    const [pluginSpaceCache, setPluginSpaceCache] = useState<any>(null)
    const [isLoadingPluginSpace, setIsLoadingPluginSpace] = useState<boolean>(false)
    
    // Controle adicional para evitar múltiplas chamadas
    const [lastProcessedId, setLastProcessedId] = useState<number | null>(null)
    const [lastApiCallTime, setLastApiCallTime] = useState<number>(0)
    
    // Constante de debounce para evitar chamadas muito frequentes (500ms)
    const API_CALL_DEBOUNCE = 500

    async function getPluginSpace(fromApi?: boolean, userLocalStorage?: boolean) {
        // SEMPRE retorna cache se existe e não é explicitamente obrigatório da API
        if (pluginSpaceCache && !fromApi) {
            console.log('🔄 Usando cache do pluginspace:', pluginSpaceCache.id || 'sem ID')
            return pluginSpaceCache
        }

        // Verifica localStorage PRIMEIRO se permitido
        const contextPluginSpace = getItem('pluginSpace')
        if (contextPluginSpace && userLocalStorage && !fromApi) {
            try {
                const cachedData = JSON.parse(contextPluginSpace)
                setPluginSpace(cachedData)
                setPluginSpaceCache(cachedData)
                console.log('💾 Carregado do localStorage:', cachedData.id || 'sem ID')
                return cachedData
            } catch (error) {
                console.warn('Erro ao parsear localStorage pluginSpace:', error)
            }
        }

        // Debounce de chamadas API muito frequentes
        const now = Date.now()
        if (now - lastApiCallTime < API_CALL_DEBOUNCE) {
            console.log('⏰ Chamada API bloqueada por debounce')
            return pluginSpaceCache || {}
        }

        // Evita múltiplas chamadas simultâneas
        if (isLoadingPluginSpace) {
            console.log('⚠️ Já está carregando pluginspace, aguardando...')
            // Aguarda até que a chamada atual termine
            let attempts = 0
            while (isLoadingPluginSpace && attempts < 50) { // máximo 5 segundos
                await new Promise(resolve => setTimeout(resolve, 100))
                attempts++
            }
            return pluginSpaceCache || {}
        }

        // Só faz chamada API se realmente necessário
        console.log('🔥 Fazendo chamada API para /pluginspace/this')
        setIsLoadingPluginSpace(true)
        setLastApiCallTime(now)
        
        try {
            const res = await GET(`/pluginspace/this`)

            if (res && res.data && res.data[0]) {
                const pluginspaceData = res.data[0]
                setPluginSpace(pluginspaceData)
                setPluginSpaceCache(pluginspaceData)
                saveItem('pluginSpace', pluginspaceData)
                console.log('✅ PluginSpace carregado com sucesso:', pluginspaceData.id || 'sem ID')
                return pluginspaceData
            }

            console.warn('⚠️ Resposta vazia da API pluginspace')
            return {}
        } catch (error) {
            console.error('❌ Erro ao carregar pluginspace:', error)
            return {}
        } finally {
            setIsLoadingPluginSpace(false)
        }
    }

    async function getGmtStore() {
        try {
            // Usa cache primeiro
            const pluginspace = await getPluginSpace(false, true)

            if (pluginspace && pluginspace.gmt) {
                return pluginspace.gmt
            } else {
                return '+00:00'
            }
        } catch (e) {
            return '+00:00'
        }
    }

    async function getPluginConfigs(update?: boolean, useLocalStorage?: boolean) {
        try {

            if (pluginConfigs && pluginConfigs.length && !update) {
                return pluginConfigs
            }


            const contextPluginConfig = getItem('pluginConfig')

            if (contextPluginConfig && useLocalStorage) {
                setPluginConfigs(JSON.parse(contextPluginConfig))

                return JSON.parse(contextPluginConfig)
            }

            const res = await POST(`/pluginconfig/filter`, {})

            if (res && res.data && res.data.length) {
                setPluginConfigs(res.data)
                setMetaPluginConfigs(res.meta)

                saveItem('pluginConfig', res.data)

                return res.data
            }

            return []
        } catch (error) {
            console.log(error)
            return []
        }
    }


    async function getLocations(fromApi?: boolean, useLocalStorage?: boolean) {
        if (locations && locations.length > 0 && !fromApi) {
            return locations
        }

        const contextLocations = getItem('locations')

        if (contextLocations && useLocalStorage) {
            setLocations(
                JSON.parse(contextLocations)
            );
            return JSON.parse(contextLocations)

        }

        const response = await GET(`/location`)

        const newLocations = response.data.map(
            (location: LocationType) => ({
                id: location.id,
                name: location.name,
                code: location.code,
                status: location.status,
                type: location.type,
                cep: location.cep,
                address: location.address
            })
        );

        setLocations(
            newLocations
        );

        saveItem('locations', newLocations)
        return newLocations

    }

    async function getMenu(fromApi?: boolean) {
        try {
            if (menu.length) {
                return menu
            }


            const response = await GET('/pages/buildMenuPage')
            if (response) {
                const menuOrder = response.sort((a: MenuType, b: MenuType) => a.order - b.order)
                setMenu(menuOrder)

                return menuOrder
            }
        } catch (error) {
            console.log(error)
        }
    }


    async function getManual(fromApi?: boolean) {
        if (manual.length && !fromApi) {
            return manual
        }
        const contextManual = getItem('manual')
        if (contextManual) {
            setManual(JSON.parse(contextManual))
            return JSON.parse(contextManual)
        }

        const response = await GET('manual')

        if (response && response.data) {
            setManual(response.data)
            return response.data
        }

    }

    async function getIntegrations(update?: boolean, useLocalStorage?: boolean) {

        try {

            let data: IntegrationType[] = []


            const contextIntegrations = getItem('integrations')

            if (integrations && integrations.length && !update) {
                data = integrations
            } else if (contextIntegrations && useLocalStorage) {
                data = JSON.parse(contextIntegrations)
            } else {

                const integrationRes = await GET(`/pluginconfig`)

                data = integrationRes.data

                saveItem('integrations', data)
            }
            setIntegrations(data)

            const findWintorIntegration = data.find(el => el.plugintemplateId === 45)

            if (findWintorIntegration) {
                setIsWintor(true)
            }

            const findProtheusIntegration = data.find(el => el.plugintemplateId === 104)

            if (findProtheusIntegration) {
                setIsProtheus(true)
            }

            const totvsIntegration = data.filter(
                (pluginconfig: any) =>
                    pluginconfig.plugintemplateId === 23 ||
                    pluginconfig.plugintemplateId === 15 ||
                    pluginconfig.plugintemplateId === 45 ||
                    // pluginconfig.plugintemplateId === 48 ||
                    pluginconfig.plugintemplateId === 104
            )

            if (totvsIntegration && totvsIntegration.length > 0)
                setHasERP(true)
            else
                setHasERP(false)

            const totvsHas = data.find(
                (integration: IntegrationType) =>
                    integration.plugintemplateId === 23 ||
                    integration.plugintemplateId === 15 ||
                    integration.plugintemplateId === 45
            )
            if (
                (totvsHas && totvsHas.content && totvsHas.content.able === '1') ||
                (totvsHas && totvsHas.plugintemplateId === 45)
            ) {
                setUseTotvs(true)
            } else {
                setUseTotvs(false)
            }

            const sdsIntegration = data.find(
                (integration: IntegrationType) =>
                    integration.plugintemplateId === 101
            )
            if (
                (sdsIntegration && sdsIntegration.content && sdsIntegration.content.able === '1') ||
                (sdsIntegration && sdsIntegration.plugintemplateId === 45)
            ) {

                setUsingSDS(true)
            }

            const synCarIntegration = data.find(
                (integration: IntegrationType) =>
                    integration.plugintemplateId === 108
            )
            if (synCarIntegration && synCarIntegration.content && synCarIntegration.content.able === '1') {
                setUsingSynkar(true)
            }

            return data

        } catch (e) {
            console.log(e)
            return []
        }

    }

    useEffect(() => {
        // Throttling mais agressivo para evitar loops
        let timeoutId: NodeJS.Timeout | null = null
        
        const loadAllData = async () => {
            try {
                console.log('🚀 Iniciando loadAllData para pluginspaceId:', loginInfo.pluginspaceId)
                
                // Evita carregar dados se não há loginInfo válido
                if (!loginInfo.pluginspaceId) {
                    console.log('❌ Sem pluginspaceId válido')
                    return
                }

                // Evita carregar se já está carregando
                if (isLoadingPluginSpace) {
                    console.log('⚠️ Já está carregando, cancelando')
                    return
                }

                // Verifica se já carregou para este pluginspace exato
                if (isDataLoaded && 
                    currentPluginSpaceId === loginInfo.pluginspaceId && 
                    lastProcessedId === loginInfo.pluginspaceId) {
                    console.log('✅ Dados já carregados para este pluginspace')
                    setLoadingGlobal(false)
                    return
                }

                // Evita executar muito rapidamente
                const now = Date.now()
                if (now - lastApiCallTime < API_CALL_DEBOUNCE * 2) { // 1 segundo de debounce para useEffect
                    console.log('⏰ UseEffect bloqueado por debounce')
                    return
                }

                setLastProcessedId(loginInfo.pluginspaceId)
                setLoadingGlobal(true)
                setLastApiCallTime(now)

                console.log('📦 Carregando dados em paralelo')
                await Promise.all([
                    getPluginSpace(false, true), // Força usar cache/localStorage primeiro
                    getMenu(true),
                    getLocations(false, true),
                    getManual(true),
                    getIntegrations(false, true),
                    getPluginConfigs(false, true),
                ])

                setCurrentPluginSpaceId(loginInfo.pluginspaceId)
                setIsDataLoaded(true)
                setLoadingGlobal(false)
                console.log('✅ Todos os dados carregados com sucesso')
            } catch (error) {
                console.error('❌ Erro no loadAllData:', error)
                setLoadingGlobal(false)
            }
        }

        // Debounce para evitar execuções muito frequentes
        const debouncedLoadData = () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
            
            timeoutId = setTimeout(() => {
                // Só carrega se tem loginInfo válido e ainda não carregou para esse pluginspaceId
                if (loginInfo.pluginspaceId && 
                    (!isDataLoaded || currentPluginSpaceId !== loginInfo.pluginspaceId)) {
                    loadAllData()
                }
            }, 100) // 100ms de debounce
        }

        debouncedLoadData()

        // Cleanup do timeout
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginInfo.pluginspaceId])


    return (
        <PluginSpaceContext.Provider
            value={{
                pluginSpace,
                getPluginSpace,
                getLocations,
                locations,
                menu,
                getMenu,
                manual,
                isProtheus,
                isWintor,
                integrations,
                hasERP,
                useTotvs,
                usingSDS,
                usingSynkar,
                getIntegrations,
                getPluginConfigs,
                metaPluginConfigs,
                loadingGlobal,
                setLoadingGlobal,
                getGmtStore,
                alterMenu,
                setAlterMenu
            }}
        >
            {children}
        </PluginSpaceContext.Provider>
    )
}

export default PluginSpaceProvider